import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface Package {
  id: string;
  name: string;
  amount: number;
  currency: string;
  allowed_modules: string[];
}

type CheckoutState =
  | 'loading' | 'idle' | 'redirecting' | 'checking' | 'awaiting'
  | 'success' | 'canceled' | 'unavailable' | 'error';

function returnDestination(sessionId: string | null): string {
  return sessionId ? `/checkout?session_id=${encodeURIComponent(sessionId)}` : '/checkout';
}

const MODULE_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  ai: 'Capital AI',
};

export default function Checkout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState<Package[]>([]);
  const [state, setState] = useState<CheckoutState>('loading');
  const [message, setMessage] = useState('');
  const [entitledModules, setEntitledModules] = useState<string[] | null>(null);
  const [pendingSessionId] = useState<string | null>(
    () => new URLSearchParams(window.location.search).get('session_id')
  );

  const refreshEntitlements = useCallback(async () => {
    try {
      const res = await fetch(`/api/entitlements/me`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setEntitledModules(data.allowed_modules || []);
      }
    } catch {
      // non-fatal; entitlements shown best-effort on this page
    }
  }, []);

  const pollStatus = useCallback(async (sessionId: string, attempts = 0) => {
    if (attempts >= 10) {
      setState('awaiting');
      setMessage('Confirmation is in progress. Your access will activate automatically once your payment is verified.');
      return;
    }
    try {
      const res = await fetch(`/api/payments/checkout/status/${sessionId}`, { credentials: 'include' });
      if (res.status === 401) {
        navigate(`/login?next=${encodeURIComponent(returnDestination(sessionId))}`);
        return;
      }
      if (res.status === 503) {
        setState('unavailable');
        setMessage('Payments are temporarily unavailable. Please try again later.');
        return;
      }
      if (!res.ok) throw new Error('status_failed');
      const data = await res.json();
      if (data.entitlement_state === 'entitled') {
        setState('success');
        setMessage('Payment confirmed. Your access has been activated.');
        refreshEntitlements();
        return;
      }
      if (data.entitlement_state === 'failed') {
        setState('error');
        setMessage('Payment could not be verified. No access was granted. Please contact support.');
        return;
      }
      if (data.entitlement_state === 'expired' || data.status === 'expired') {
        setState('canceled');
        setMessage('This payment session expired. No charge was made.');
        return;
      }
      setState('checking');
      setMessage(
        data.entitlement_state === 'paid_processing'
          ? 'Payment received. Awaiting secure confirmation — access activates once verified.'
          : 'Payment is being processed...'
      );
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    } catch {
      setState('error');
      setMessage('Unable to verify payment status. Please try again.');
    }
  }, [refreshEntitlements, navigate]);

  useEffect(() => {
    fetch(`/api/entitlements/packages`)
      .then(res => res.json())
      .then(data => {
        setPackages(data.packages || []);
        setState(prev => (prev === 'loading' ? 'idle' : prev));
      })
      .catch(() => {
        setState('error');
        setMessage('Unable to load packages. Please try again later.');
      });

    if (new URLSearchParams(window.location.search).get('canceled') === 'true') {
      setState('canceled');
      setMessage('Checkout was canceled. No charge was made and no access was granted.');
    }
  }, []);

  useEffect(() => {
    if (!pendingSessionId) return;
    if (user === undefined) {
      setState('checking');
      setMessage('Verifying your session...');
      return;
    }
    if (user === null) {
      navigate(`/login?next=${encodeURIComponent(returnDestination(pendingSessionId))}`);
      return;
    }
    setState('checking');
    setMessage('Verifying your payment...');
    pollStatus(pendingSessionId);
  }, [user, pendingSessionId, pollStatus, navigate]);

  useEffect(() => {
    if (user) refreshEntitlements();
  }, [user, refreshEntitlements]);

  async function handlePay(packageId: string) {
    setState('redirecting');
    setMessage('Preparing secure checkout...');
    try {
      const res = await fetch(`/api/payments/checkout/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ package_id: packageId, origin_url: window.location.origin })
      });
      if (res.status === 503) {
        setState('unavailable');
        setMessage('Payments are temporarily unavailable. Please try again later.');
        return;
      }
      if (res.status === 401) {
        setState('idle');
        setMessage('Please log in to purchase a package.');
        return;
      }
      if (!res.ok) throw new Error('session_failed');
      const data = await res.json();
      if (!data.url) throw new Error('no_url');
      window.location.href = data.url;
    } catch {
      setState('error');
      setMessage('Unable to start checkout. Please try again.');
    }
  }

  const authed = !!user;
  const isBusy = state === 'redirecting' || state === 'checking' || state === 'loading';

  return (
    <div className="checkout-page" data-testid="checkout-page">
      <div className="checkout-container">
        <img src="/brand/nwg-logo-small.png" alt="NextWealth Global — Show Us the Problems. We Build the Solutions." className="checkout-logo" />
        <h1 className="checkout-title">NWG Command Center — Secure Checkout</h1>
        <p className="checkout-subtitle">TEST MODE — test configurations only, no real charges. Powered by Stripe.</p>

        {message && (
          <div className={`checkout-status checkout-status-${state}`} data-testid="checkout-status">
            {message}
          </div>
        )}

        {state === 'success' && (
          <button
            className="checkout-pay-btn"
            onClick={() => navigate('/')}
            data-testid="enter-dashboard-btn"
          >
            Enter your Dashboard →
          </button>
        )}

        {state === 'awaiting' && pendingSessionId && (
          <button
            className="checkout-pay-btn"
            onClick={() => {
              setState('checking');
              setMessage('Checking confirmation status...');
              pollStatus(pendingSessionId);
            }}
            data-testid="check-again-btn"
          >
            Check again
          </button>
        )}

        {!authed && user !== undefined && (
          <div className="checkout-status" data-testid="checkout-login-required">
            An account is required to purchase.{' '}
            <Link
              to={`/login?next=${encodeURIComponent(returnDestination(pendingSessionId))}`}
              className="checkout-inline-link"
              data-testid="checkout-login-link"
            >
              Log in
            </Link>{' '}
            or{' '}
            <Link
              to={`/register?next=${encodeURIComponent(returnDestination(pendingSessionId))}`}
              className="checkout-inline-link"
              data-testid="checkout-register-link"
            >
              register
            </Link>{' '}
            to continue.
          </div>
        )}

        {authed && entitledModules && entitledModules.length > 0 && (
          <div className="checkout-status checkout-status-success" data-testid="checkout-owned">
            Active access: {entitledModules.map(m => MODULE_LABELS[m] || m).join(', ')}
          </div>
        )}

        {state !== 'success' && (
          <div className="checkout-packages">
            {packages.map(pkg => (
              <div key={pkg.id} className="checkout-package" data-testid={`package-${pkg.id}`}>
                <div className="checkout-package-name">{pkg.name}</div>
                <div className="checkout-package-price">
                  ${pkg.amount.toFixed(2)} <span>{pkg.currency.toUpperCase()}</span>
                </div>
                <div className="checkout-package-modules">
                  Unlocks: {pkg.allowed_modules.map(m => MODULE_LABELS[m] || m).join(' + ')}
                </div>
                <button
                  className="checkout-pay-btn"
                  onClick={() => handlePay(pkg.id)}
                  disabled={!authed || isBusy}
                  data-testid={`pay-btn-${pkg.id}`}
                >
                  {state === 'redirecting' ? 'Redirecting...' : authed ? 'Pay with Stripe' : 'Login required'}
                </button>
              </div>
            ))}
          </div>
        )}

        <Link to="/" className="checkout-back-link" data-testid="checkout-back-link">
          ← Return to NWG Command Center
        </Link>
        <p className="checkout-subtitle" data-testid="checkout-powered-by">Powered by NextWealth™ Global</p>
      </div>
    </div>
  );
}
